import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  LabelList
} from 'recharts';

export default function CompanyPerformance({
  activeTab = 'Bookings',
  setActiveTab,
  companyComparison = []
}) {
  const [currentTab, setCurrentTab] = useState(activeTab || 'Bookings');

  // Tabs: Bookings, Duty Slips, Invoices
  const tabs = ['Bookings', 'Duty Slips', 'Invoices'];

  const dynamicDatasetByTab = {
    'Bookings': Array.isArray(companyComparison)
      ? companyComparison
          .map((c) => ({ company: c.company, value: Number(c.bookings) || 0 }))
          .filter((c) => c.company)
      : [],
    'Duty Slips': Array.isArray(companyComparison)
      ? companyComparison
          .map((c) => ({ company: c.company, value: Number(c.dutySlips) || 0 }))
          .filter((c) => c.company)
      : [],
    'Invoices': Array.isArray(companyComparison)
      ? companyComparison
          .map((c) => ({ company: c.company, value: Number(c.invoices) || 0 }))
          .filter((c) => c.company)
      : []
  };

  const rawData = dynamicDatasetByTab[currentTab] || dynamicDatasetByTab['Bookings'] || [];
  // Sort current dataset descending by selected tab value, then slice top 6
  const currentData = [...rawData].sort((a, b) => b.value - a.value).slice(0, 6);

  // Dynamic max for X-axis so bars stretch across the container
  const maxVal = Math.max(...currentData.map((d) => d.value), 2);
  const xDomainMax = Math.ceil(maxVal * 1.25);

  const handleTabClick = (t) => {
    setCurrentTab(t);
    if (setActiveTab) setActiveTab(t);
  };

  return (
    <div className="dash-card p-2 d-flex flex-column justify-content-between" style={{ height: '100%' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Card Header */}
        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
          <h6 className="m-0" style={{ fontSize: '12.5px', fontWeight: '800', color: '#0f172a' }}>
            Company Comparison
          </h6>
          <span
            className="badge"
            style={{
              fontSize: '10px',
              fontWeight: '600',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              padding: '2px 6px'
            }}
          >
            {currentTab}
          </span>
        </div>

        {/* Tabs Row */}
        <div className="d-flex justify-content-between align-items-center mb-2 px-1 flex-wrap gap-1">
          <div className="d-flex gap-1 flex-wrap">
            {tabs.map((t) => {
              const isActive = currentTab === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleTabClick(t)}
                  style={{
                    border: isActive ? 'none' : '1px solid #cbd5e1',
                    backgroundColor: isActive ? '#064e48' : '#ffffff',
                    color: isActive ? '#ffffff' : '#334155',
                    padding: '2px 10px',
                    borderRadius: '4px',
                    fontSize: '10.5px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Horizontal Bar Chart / Empty state */}
        <div style={{ width: '100%', flex: 1, minHeight: 180 }}>
          {currentData.length === 0 || currentData.every((d) => d.value === 0) ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center h-100"
              style={{ minHeight: 180, color: '#94a3b8', fontSize: '12px' }}
            >
              <i className="fa-solid fa-chart-simple mb-2" style={{ fontSize: '24px', opacity: 0.5 }}></i>
              <span>No {currentTab.toLowerCase()} data for selected filters</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={currentData}
                margin={{ top: 4, right: 28, left: 6, bottom: 2 }}
                barCategoryGap={6}
              >
                <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, xDomainMax]}
                  tickCount={5}
                  tick={{ fontSize: 9, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="company"
                  width={130}
                  interval={0}
                  tick={{ fontSize: 10, fill: '#1e293b', fontWeight: 600 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  tickFormatter={(val) => (val && val.length > 22 ? val.substring(0, 20) + '...' : val)}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: '11px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    padding: '4px 8px'
                  }}
                  formatter={(val) => [val, currentTab]}
                />
                <Bar
                  dataKey="value"
                  fill="#0d7a71"
                  barSize={10}
                  radius={[0, 5, 5, 0]}
                >
                  <LabelList
                    dataKey="value"
                    position="right"
                    style={{ fontSize: '10.5px', fill: '#0f172a', fontWeight: '700' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Footer matching adjacent pagination bars for equal bottom alignment */}
      <div className="d-flex justify-content-between align-items-center mt-2 pt-1 border-top px-1" style={{ fontSize: '10px', color: '#64748b' }}>
        <span>Top {currentData.length} Parties</span>
        <span style={{ fontWeight: 600, color: '#0d7a71' }}>Filtered Period</span>
      </div>
    </div>
  );
}
